import React, { useEffect, useRef, useState } from 'react';
import { CaretDown, FunnelSimple, MagnifyingGlass, Plus, X } from '@phosphor-icons/react';
import {
  FILTER_PANEL_CLASSES,
  FILTER_FIELD_CLASSES,
  FILTER_LABEL_CLASSES,
  FILTER_HEADER_CLASSES,
  FILTER_HEADER_TITLE_CLASSES,
} from '../../../shared/components/ui/filterStyles';

const OPERATOR_LABELS = {
  contains: 'contiene',
  eq: 'es igual a'
};

const FormResponseFieldFilters = ({ fields = [], filters = [], onAdd, onRemove }) => {
  const [draft, setDraft] = useState({ field: '', op: 'contains', value: '' });
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const selectedField = fields.find((field) => field.id === draft.field);
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const visibleFields = fields.filter((field) => {
    if (!normalizedSearch) return true;
    const formNames = (field.forms || []).map((form) => form.name).join(' ');
    return `${field.label} ${field.id} ${formNames}`.toLocaleLowerCase().includes(normalizedSearch);
  });

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  useEffect(() => {
    if (isOpen) searchRef.current?.focus();
  }, [isOpen]);

  const chooseField = (field) => {
    setDraft((current) => ({ ...current, field: field.id }));
    setSearch('');
    setIsOpen(false);
  };

  const addFilter = () => {
    if (!draft.field || !draft.value.trim()) return;
    onAdd({ ...draft, value: draft.value.trim() });
    setDraft({ field: '', op: 'contains', value: '' });
  };

  return (
    <section className={FILTER_PANEL_CLASSES} aria-label="Filtrar por campo del formulario">
      <div className="px-6 py-4">
        <div className={FILTER_HEADER_CLASSES}>
          <FunnelSimple size={18} weight="bold" className="text-content" />
          <h4 className={FILTER_HEADER_TITLE_CLASSES}>Filtrar por campo del formulario</h4>
        </div>
      </div>

      <div className="px-6 pb-6">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1.45fr)_minmax(160px,.55fr)_minmax(0,1.45fr)_auto] md:items-end">
          <div ref={containerRef} className="relative min-w-0">
            <label htmlFor="form-response-field" className={FILTER_LABEL_CLASSES}>Campo</label>
            <button
              id="form-response-field"
              type="button"
              onClick={() => setIsOpen((open) => !open)}
              className={`${FILTER_FIELD_CLASSES} flex items-center justify-between text-left hover:border-content-muted`}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
            >
              <span className={selectedField ? 'truncate text-content' : 'text-content-muted'}>
                {selectedField ? selectedField.label : 'Selecciona un campo'}
              </span>
              <CaretDown size={17} className="ml-2 shrink-0 text-content-muted" />
            </button>

            {isOpen && (
              <div className="absolute z-30 mt-2 w-full overflow-hidden rounded-lg border border-line bg-surface shadow-2xl">
                <div className="border-b border-line p-2">
                  <div className="flex items-center gap-2 rounded-md border border-line bg-surface-2 px-2">
                    <MagnifyingGlass size={16} className="shrink-0 text-content-muted" />
                    <input
                      ref={searchRef}
                      type="search"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') setIsOpen(false);
                      }}
                      className="h-9 w-full bg-transparent text-sm text-content placeholder:text-content-muted focus:outline-none"
                      placeholder="Buscar campo..."
                      aria-label="Buscar campo"
                    />
                  </div>
                </div>
                <ul className="max-h-64 overflow-y-auto py-1" role="listbox" aria-label="Campos disponibles">
                  {visibleFields.length === 0 ? (
                    <li className="px-3 py-3 text-sm text-content-muted">No se encontraron campos.</li>
                  ) : visibleFields.map((field) => (
                    <li key={field.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={draft.field === field.id}
                        onClick={() => chooseField(field)}
                        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm hover:bg-surface-2 focus:bg-surface-2 focus:outline-none"
                      >
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-content">{field.label}</span>
                          <span className="block truncate text-xs text-content-muted">
                            {field.source === 'root'
                              ? 'Campo generico del formulario'
                              : (field.forms || []).length > 0
                              ? `Formulario: ${(field.forms || []).map((form) => form.name).join(' · ')}`
                              : 'Formulario no disponible'}
                          </span>
                        </span>

                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div>
            <label htmlFor="form-response-operator" className={FILTER_LABEL_CLASSES}>Condición</label>
            <select
              id="form-response-operator"
              value={draft.op}
              onChange={(event) => setDraft((current) => ({ ...current, op: event.target.value }))}
              className={FILTER_FIELD_CLASSES}
            >
              <option value="contains">contiene</option>
              <option value="eq">es igual a</option>
            </select>
          </div>

          <div>
            <label htmlFor="form-response-value" className={FILTER_LABEL_CLASSES}>Valor</label>
            <input
              id="form-response-value"
              type="text"
              value={draft.value}
              onChange={(event) => setDraft((current) => ({ ...current, value: event.target.value }))}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addFilter();
                }
              }}
              placeholder="Ingresa un valor"
              className={FILTER_FIELD_CLASSES}
            />
          </div>

          <button
            type="button"
            onClick={addFilter}
            disabled={!draft.field || !draft.value.trim()}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-amber px-4 text-sm font-medium text-on-amber transition-colors hover:bg-amber-hover disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-content-muted"
          >
            <Plus size={17} weight="bold" />
            Agregar
          </button>
        </div>

        {filters.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-line pt-3">
            <span className="text-xs text-content-muted">Filtros activos:</span>
            {filters.map((filter, index) => (
              <span key={`${filter.field}-${filter.op}-${filter.value}-${index}`} className="inline-flex max-w-full items-center gap-1 rounded-full border border-line bg-surface-2 px-3 py-1 text-xs text-content">
                <strong className="truncate text-content">{filter.label || filter.field}</strong>
                <span className="text-content-muted">{OPERATOR_LABELS[filter.op]}</span>
                <strong className="max-w-[180px] truncate font-mono text-content">{filter.value}</strong>
                <button
                  type="button"
                  onClick={() => onRemove(index)}
                  className="ml-1 rounded-full text-content-muted hover:text-content focus:outline-none focus:ring-2 focus:ring-amber"
                  aria-label={`Quitar filtro ${filter.label || filter.field}`}
                >
                  <X size={14} weight="bold" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FormResponseFieldFilters;
